// HPC Report - PartDPage3 (Learner Reflection - Read-only, A4 for printing)
import React from 'react';

const PartDPage3 = ({ data }) => {
  const studentFields = data?.student_fields || {};

  const getTotalChecked = (category) => {
    if (!category) return 0;
    return [
      category.item_1_checked,
      category.item_2_checked,
      category.item_3_checked
    ].filter(Boolean).length;
  };

  const renderAssessmentCategory = (category, title, items) => {
    const categoryData = studentFields[category] || {};
    const totalChecked = getTotalChecked(categoryData);

    return (
      <div style={{ marginBottom: '15px' }}>
        {/* Category Header */}
        <div style={{
          backgroundColor: '#87CEEB',
          color: '#000',
          padding: '8px 15px',
          fontSize: '11px',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '8px'
        }}>
          {title}
        </div>

        {/* Content Area */}
        <div style={{
          backgroundColor: '#E6F3FF',
          padding: '12px'
        }}>
          {/* Checkbox Items */}
          {items.map((text, idx) => {
            const itemNum = idx + 1;
            const isChecked = categoryData[`item_${itemNum}_checked`];

            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #666',
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  {isChecked ? '✓' : ''}
                </div>
                <div style={{ flex: 1, fontSize: '10px', lineHeight: '1.4' }}>
                  {text}
                </div>
              </div>
            );
          })}

          {/* Total Count */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
            <span style={{ fontSize: '10px', fontWeight: 'bold' }}>Total number of (✓):</span>
            <div style={{
              width: '35px',
              height: '35px',
              borderRadius: '50%',
              border: '3px solid #4A7BA7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              backgroundColor: 'white'
            }}>
              {totalChecked}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="report-page" style={{
      width: '210mm',
      minHeight: '297mm',
      margin: '0 auto',
      padding: '20mm',
      backgroundColor: 'white',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      position: 'relative',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    }}>
      {/* Top-right corner */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 80px 80px 0',
        borderColor: 'transparent #F4824C transparent transparent'
      }} />

      {/* Header */}
      <div style={{
        fontSize: '15px',
        fontWeight: 'bold',
        textAlign: 'center',
        backgroundColor: '#4A7BA7',
        color: 'white',
        padding: '10px',
        marginBottom: '15px'
      }}>
        Learner Reflection
      </div>

      {/* Instruction */}
      <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '12px' }}>
        Tick (✓) all statements that apply.
      </div>

      {/* Three Assessment Categories */}
      {renderAssessmentCategory('awareness', 'Awareness', [
        'I was sufficiently prepared for the activity.',
        'I demonstrated a solid understanding of the key concepts covered.',
        'I changed my approach as per the changing dynamics of the activity.'
      ])}

      {renderAssessmentCategory('sensitivity', 'Sensitivity', [
        'I actively considered different perspectives during the activity.',
        'I acknowledged the feelings and opinions of my peers.',
        'I gave constructive feedback in a considerate manner.'
      ])}

      {renderAssessmentCategory('creativity', 'Creativity', [
        'I approached challenges with a willingness to explore unique solutions.',
        'I demonstrated flexibility in my thinking.',
        'I actively sought inspiration from different sources to improve my contribution.'
      ])}

      {/* Self-Reflection Section */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
        {/* Stick Figure + Speech Bubble */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
          {/* Stick Figure */}
          <svg width="50" height="70" viewBox="0 0 50 70" style={{ marginTop: '10px' }}>
            <circle cx="25" cy="15" r="12" fill="none" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="27" x2="25" y2="45" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="45" x2="15" y2="65" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="45" x2="35" y2="65" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="32" x2="10" y2="40" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="32" x2="40" y2="40" stroke="#000" strokeWidth="2" />
          </svg>

          {/* Arrow from speech bubble */}
          <svg width="30" height="20" viewBox="0 0 30 20" style={{ marginLeft: '50px', marginTop: '-10px' }}>
            <path d="M 0 10 L 25 10 M 25 10 L 20 5 M 25 10 L 20 15" stroke="#000" strokeWidth="2" fill="none" />
          </svg>
        </div>

        {/* Speech Bubble */}
        <div style={{
          border: '2px solid #000',
          borderRadius: '15px',
          padding: '12px',
          backgroundColor: 'white',
          fontSize: '9px',
          fontWeight: 'bold',
          textAlign: 'center',
          maxWidth: '180px',
          position: 'relative',
          marginTop: '10px'
        }}>
          Leave some words of appreciation/encouragement for yourself.
        </div>

        {/* Self-Reflection Textarea */}
        <div style={{
          flex: 1,
          backgroundColor: '#E6F3FF',
          padding: '12px',
          minHeight: '100px',
          fontSize: '10px',
          lineHeight: '1.5',
          borderRadius: '8px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {studentFields.self_reflection || ''}
        </div>
      </div>

      {/* Page Number */}
      <div style={{
        position: 'absolute',
        bottom: '15mm',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: '#666',
        fontWeight: 'bold'
      }}>
        33
      </div>

      {/* Bottom corners */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 0 0 60px',
        borderColor: 'transparent transparent transparent #F4824C'
      }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 60px 0 0',
        borderColor: 'transparent #F4824C transparent transparent',
        opacity: 0.3
      }} />
    </div>
  );
};

export default PartDPage3;
